import React from 'react';

import {
  Flex,
  IconButton,
  rem,
  type BoxProps,
  type FlexProps,
  type IconButtonProps,
} from '@vimeo/bokeh';
import { ArrowLeft } from '@vimeo/bokeh-icons';

import { Upgrade } from '../Upgrade';
import { VimeoLogo } from '../VimeoLogo';

/**
 * The `Navigation` component provides the shell of the top navigation bar.
 *
 * This component wraps the Bokeh Flex component and takes in `FlexProps`
 *
 * @example
 *
 * Base usage
 * ```tsx
 * <Navigation>
 *  <Navigation.LeftContent>
 *    <Navigation.VimeoLogo />
 *  </Navigation.LeftContent>
 *
 *  <Navigation.RightContent>
 *    // Other page specific actions
 *    <AccountMenu />
 *  </Navigation.RightContent>
 * </Navigation>
 * ```
 *
 * Make the top nav sticky
 * ```tsx
 * <Navigation position="sticky" top="50px">
 *  <Navigation.LeftContent>
 *    <Navigation.VimeoLogo />
 *  </Navigation.LeftContent>
 *  <Navigation.RightContent>
 *    // Other page specific actions
 *    <AccountMenu />
 *  </Navigation.RightContent>
 * </Navigation>
 * ```
 */
export function Navigation({ children, ...props }: React.PropsWithChildren<BoxProps>) {
  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      width="100%"
      paddingX="lg"
      bgColor="background"
      backdropFilter="var(--vimeo-blur-lg)"
      minH="2xl"
      {...props}
      sx={{
        ...props.sx,
      }}
    >
      {children}
    </Flex>
  );
}

/**
 * Align content to the left side of the navigation bar.
 */
const LeftContent = ({
  children,
  align = 'center',
  gap = 'sm',
  ...props
}: React.PropsWithChildren<FlexProps>) => {
  return (
    <Flex align={align} gap={gap} {...props}>
      {children}
    </Flex>
  );
};

/**
 * Align content to the right side of the navigation bar.
 */
const RightContent = ({
  children,
  align = 'center',
  gap = 'md',
  ...props
}: React.PropsWithChildren<FlexProps>) => {
  return (
    <Flex align={align} gap={gap} {...props}>
      {children}
    </Flex>
  );
};

/**
 * Icon button with an ArrowLeft icon and secondary variant as the default.
 */
const GoBackButton = ({
  icon = <ArrowLeft />,
  variant = 'secondary',
  ...props
}: IconButtonProps) => {
  return <IconButton icon={icon} variant={variant} {...props} />;
};

Navigation.LeftContent = LeftContent;
Navigation.RightContent = RightContent;
Navigation.GoBackButton = GoBackButton;
Navigation.VimeoLogo = VimeoLogo;
Navigation.Upgrade = Upgrade;
